<template>
    <div class="verify-page">
        <div class="verify-page__container">
            <logos-container class="verify-page__logos" />
        <h1 class="verify-page__title">{{ $t('Two-Factor Authentication') }}</h1>
        <p class="verify-page__text">{{ $t('Open the two-step verification app on your mobile device to get your verification code.') }}</p>
        <div>
            <form @submit.prevent="verify()">
                <div class="mb-3">
                    <ui-input
                        id="code"
                        name="code"
                        type="number"
                        placeholder="000000"
                        v-model="token"
                        :label="$t('Code')"
                        :field="v$.token"
                    />
                </div>
                <ui-button :loading="loading" class="w-100 mb-0" type="submit" variant="primary">
                    {{ $t('Validate code') }}
                </ui-button>
            </form>
            <div class="text-left mt-4">
                <b-button :disabled="sendingEmail" variant="link" type="button" class="verify-page__send-code" @click="sendEmail">
                    <span v-if="sendingEmail" class="ui-button__loader fas fa-spinner fa-spin"></span>
                    {{ $t('Send the code to my email') }}
                </b-button>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, maxLength, minLength, helpers } from '@vuelidate/validators';

import UiInput from '@/components/ui/inputs/Input.vue';
import UiButton from '@/components/ui/buttons/Button.vue';
import LogosContainer from '@/components/ui/LogosContainer.vue';

import webViewMixin from '@/plugins/mixin/web-view.mixin';

export default {
    components: { UiInput, UiButton, LogosContainer },
    mixins: [webViewMixin],
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            loading: false,
            sendingEmail: false,
            token: '',
            isCodeFromEmail: false,
            user: null,
            loadingAccount: false,
        };
    },
    validations() {
        return {
            token: {
                required: helpers.withMessage(this.$t('validations.required', { field: this.$t('code') }), required),
                minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params ), minLength(6)),
                maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params ), maxLength(6)),
                $autoDirty: true,
            },
        };
    },
    mounted() {
        this.getAccountDetails();
    },
    methods: {
        async getAccountDetails() {
            try {
                this.loadingAccount = true;

                const { data } = await this.axiosAccount.get('/api/accounts/me');

                this.user = data;
            } catch(error) {
                this.handleErrors(error, 'accounts');
            } finally {
                this.loadingAccount = false;
            }
        },
        async verify() {
            try {
                this.loading = true;

                this.v$.$touch();

                if (this.v$.$invalid) {
                    return;
                }

                const token = `${this.user.email}:${this.token}`;
                const encodedToken = Buffer.from(token).toString('base64');
                const config = { headers: { 'Authorization': `Basic ${encodedToken}` } };

                const payload = {
                    token: ''
                };

                if (this.isCodeFromEmail) {
                    payload.type = 'email';
                }

                const { data } = await this.axiosAccount.post('/api/login/tfa', payload, config);
                let { redirect_url } = data;
                const webview = this.$route.query.webview;

                if (webview) {
                    redirect_url = this.formatWebviewRedirectUrl(redirect_url);
                }

                window.location.replace(redirect_url);
            } catch(error) {
                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
        async sendEmail() {
            try {
                this.sendingEmail = true;
                this.isCodeFromEmail = true;
                const payload = {
                    "type": "email",
                    "message": "Two Factor Authentication",
                    "action":"email_twofactor",
                    "from": "Accounts Tendencys"
                }
                await this.axiosAccount.post('/api/notifications/tfa', payload);
                this.$toasted.global.successMessage('Email sent successfully');
            } catch(error) {
                this.showError(error);
            } finally {
                this.sendingEmail = false;
            }
        }
    },
};
</script>

<style lang="scss">
.verify-page {
    align-items: center;

    &__container {
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
    }

    &__logos {
        display: flex;
        justify-content: center;
    }
    &__title {
        font-size: 35px;
        font-weight: 600;
        text-align: center;
        margin: 20px;
    }

    &__text {
        color: #787F84;
        font-size: 16px;
        margin: 20px;
        text-align: center;
    }

    &__send-code {
        color: $accounts-secondary !important;
        font-weight: 500 !important;
        padding: 10px !important;
        text-align: center;
    }
}
</style>
